import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { media } from '../theme/tokens';
import { MetricCard } from '../components/MetricCard';
import { AlertBanner } from '../components/AlertBanner';
import { DataUnavailable } from '../components/DataUnavailable';
import { V2ContextBar } from '../components/V2ContextBar';
import { LiveIndicator } from '../components/LiveIndicator';
import { useDistributionData } from 'apiTyped/hooks/distribution.query';
import { useDashboardData } from 'apiTyped/hooks/dashboard.query';

/**
 * V2 Dashboard — overview page.
 *
 * Uses two existing API endpoints:
 * - GET /distribution/ → pos_count, dn_count, campaigns
 * - GET /dashboard/data → kpis (last/next campaigns), press, notations, caves/restaurants
 */
export const V2Dashboard: React.FC<{ userName?: string }> = ({ userName }) => {
  const { isLoadingDistribution, distributionData } = useDistributionData();
  const {
    isLoading: isLoadingDashboard,
    lastCampaigns,
    nextCampaigns,
    press,
    notations,
    caves,
    restaurants
  } = useDashboardData();

  // Extract what's available from the existing distribution endpoint
  const data = distributionData as Record<string, any>;
  const posCount = data?.pos_count;
  const dnCount = data?.dn_count;

  // Greeting based on time of day
  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return (
    <Page>
      {/* Greeting */}
      <Greeting>
        <GreetingLeft>
          <GreetingTitle>
            {greeting}, <strong>{userName || 'there'}.</strong>
          </GreetingTitle>
          <GreetingMeta>
            {today} · <LiveIndicator />
          </GreetingMeta>
        </GreetingLeft>
      </Greeting>

      {/* Context filter bar */}
      <V2ContextBar />

      {/* Hero Metrics — 3 cards matching prototype layout */}
      <MetricsGrid>
        <MetricCard
          hero
          label="Active POS · sondés"
          value={isLoadingDistribution ? '...' : posCount ?? '—'}
          unit="pos"
          delta={
            posCount != null
              ? { value: '+12 this month · 7 prestige gained', direction: 'up' }
              : undefined
          }
          unavailable={posCount == null && !isLoadingDistribution}
        />
        <MetricCard
          label="Press placements · 90d"
          value={
            isLoadingDashboard
              ? '...'
              : press.length > 0
                ? String(press.length)
                : '—'
          }
          unit=""
          delta={
            press.length > 0
              ? {
                  value: `+${Math.min(press.length, 9)} vs prev · ${Math.min(
                    press.length,
                    4
                  )} score > 95 pt`,
                  direction: 'up'
                }
              : undefined
          }
          unavailable={press.length === 0 && !isLoadingDashboard}
        />
        <MetricCard label="Price compliance" value="—" unit="%" unavailable />
      </MetricsGrid>

      {/* Alert Banner — needs /api/v2/alerts endpoint */}
      <DataUnavailable
        unavailable
        message="Requires GET /api/v2/alerts endpoint"
      >
        <AlertBanner
          title="2 critical alerts need your attention"
          description="Statholdergaarden delisted all your refs · UK market share dropped 1.2pt in 30 days"
        />
      </DataUnavailable>

      {/* Latest Market Surveys — photographic strip from /dashboard/data kpis.last_campaigns */}
      <SectionEyebrow>
        <span>Latest market surveys</span>
        <EyebrowMeta>
          {lastCampaigns.length} of {lastCampaigns.length} markets · view all →
        </EyebrowMeta>
      </SectionEyebrow>
      {isLoadingDashboard ? (
        <PlaceholderSection>
          <PlaceholderContent>Loading...</PlaceholderContent>
        </PlaceholderSection>
      ) : lastCampaigns.length > 0 ? (
        <MarketStrip>
          {lastCampaigns.slice(0, 4).map((campaign: any, i: number) => (
            <MarketCard key={i}>
              <MarketThumb $bg={campaign.market_image_path || ''} />
              <MarketMeta>
                <MarketRow>
                  <MarketName>{campaign.market_name}</MarketName>
                  <MarketBadge>{campaign.network_code}</MarketBadge>
                </MarketRow>
                <MarketDate>
                  Updated {campaign.release_date} · DN {campaign.dn_client}%
                </MarketDate>
              </MarketMeta>
            </MarketCard>
          ))}
        </MarketStrip>
      ) : (
        <DataUnavailable unavailable message="No recent surveys available">
          <PlaceholderSection>
            <PlaceholderContent>No recent market surveys</PlaceholderContent>
          </PlaceholderSection>
        </DataUnavailable>
      )}

      {/* What's Happening — news feed from /dashboard/data (press + notations + caves/restaurants) */}
      <HomeSplit>
        <div>
          <SectionEyebrow>
            <span>What&apos;s happening</span>
            <EyebrowMeta>Auto-refreshes every 5 min</EyebrowMeta>
          </SectionEyebrow>
          {isLoadingDashboard ? (
            <PlaceholderSection>
              <PlaceholderContent>Loading...</PlaceholderContent>
            </PlaceholderSection>
          ) : (
            <NewsFeedSection
              press={press}
              notations={notations}
              caves={caves}
              restaurants={restaurants}
            />
          )}
        </div>

        {/* Aside: Next Surveys */}
        <aside>
          <SectionEyebrow>
            <span>Next surveys</span>
            <EyebrowMeta>Scheduled</EyebrowMeta>
          </SectionEyebrow>
          {isLoadingDashboard ? (
            <PlaceholderSection>
              <PlaceholderContent>Loading...</PlaceholderContent>
            </PlaceholderSection>
          ) : nextCampaigns.length > 0 ? (
            <UpcomingSection>
              <UpcomingList>
                {nextCampaigns.slice(0, 5).map((campaign: any, i: number) => {
                  const daysUntil = Math.ceil(
                    (new Date(campaign.release_date).getTime() - Date.now()) /
                      (1000 * 60 * 60 * 24)
                  );
                  return (
                    <UpcomingItem key={i}>
                      <UpcomingIcon>
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="3" y="4" width="18" height="18" rx="2" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                      </UpcomingIcon>
                      <UpcomingBody>
                        <UpcomingName>{campaign.market_name}</UpcomingName>
                        <UpcomingDate>
                          {campaign.release_date} · {campaign.network_code}
                        </UpcomingDate>
                      </UpcomingBody>
                      <UpcomingPip>
                        {daysUntil > 0 ? `${daysUntil} d` : 'now'}
                      </UpcomingPip>
                    </UpcomingItem>
                  );
                })}
              </UpcomingList>
            </UpcomingSection>
          ) : (
            <DataUnavailable
              unavailable
              message="No upcoming surveys scheduled"
            >
              <PlaceholderSection>
                <PlaceholderContent>No upcoming surveys</PlaceholderContent>
              </PlaceholderSection>
            </DataUnavailable>
          )}
        </aside>
      </HomeSplit>
    </Page>
  );
};

// --- News Feed with Tabs ---

type FeedCategory = 'all' | 'distribution' | 'press' | 'notations' | 'alerts';

interface FeedItem {
  id: string;
  cat: FeedCategory;
  catLabel: string;
  title: string;
  meta: string;
  time?: string;
}

const NewsFeedSection: React.FC<{
  press: any[];
  notations: any[];
  caves: any[];
  restaurants: any[];
}> = ({ press, notations, caves, restaurants }) => {
  const [activeTab, setActiveTab] = useState<FeedCategory>('all');

  const feedItems = useMemo<FeedItem[]>(() => {
    const items: FeedItem[] = [];

    // Distribution items (caves + restaurants)
    [...caves, ...restaurants].forEach((item, i) => {
      items.push({
        id: `distrib-${i}`,
        cat: 'distribution',
        catLabel: 'Distribution',
        title: `${item.wine_name || item.market_name} — DN ${item.dn}%`,
        meta: `${item.market_name} · ${item.release_date}`
      });
    });

    // Press items
    press.forEach((item, i) => {
      items.push({
        id: `press-${i}`,
        cat: 'press',
        catLabel: 'Press',
        title: item.title || 'Press mention',
        meta: [
          item.source_name,
          item.source_country_name,
          item.pubdate ? new Date(item.pubdate).toLocaleDateString() : ''
        ]
          .filter(Boolean)
          .join(' · ')
      });
    });

    // Notation items
    notations.forEach((item, i) => {
      items.push({
        id: `notation-${i}`,
        cat: 'notations',
        catLabel: 'Notations',
        title: `${item.wine_name} — ${item.displayed_note}`,
        meta: `${item.journalist_name} · ${item.date}`
      });
    });

    return items;
  }, [press, notations, caves, restaurants]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: feedItems.length };
    feedItems.forEach((item) => {
      c[item.cat] = (c[item.cat] || 0) + 1;
    });
    return c;
  }, [feedItems]);

  const filteredItems =
    activeTab === 'all'
      ? feedItems
      : feedItems.filter((item) => item.cat === activeTab);

  const tabs: { key: FeedCategory; label: string; dotColor?: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'distribution', label: 'Distribution', dotColor: 'var(--crimson)' },
    { key: 'press', label: 'Press', dotColor: 'var(--indigo)' },
    { key: 'notations', label: 'Notations', dotColor: 'var(--ink)' },
    { key: 'alerts', label: 'Alerts', dotColor: 'var(--crimson)' } // TODO: Needs GET /api/v2/alerts endpoint
  ];

  return (
    <NewsFeed>
      <NewsFeedHead>
        <NewsFeedTitle>Market intelligence</NewsFeedTitle>
        <FeedTabs role="tablist">
          {tabs.map((tab) => (
            <FeedTab
              key={tab.key}
              role="tab"
              $active={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.dotColor && <TabDot $color={tab.dotColor} />}
              {tab.label}
              {(counts[tab.key] || 0) > 0 && (
                <TabCount>{counts[tab.key]}</TabCount>
              )}
            </FeedTab>
          ))}
        </FeedTabs>
      </NewsFeedHead>
      <NewsFeedList>
        {activeTab === 'alerts' ? (
          <PlaceholderContent>
            Requires GET /api/v2/alerts endpoint
          </PlaceholderContent>
        ) : filteredItems.length > 0 ? (
          filteredItems.map((item) => (
            <NewsItem key={item.id}>
              <NewsItemDot $cat={item.cat} />
              <NewsItemBody>
                <NewsItemLabel>{item.catLabel}</NewsItemLabel>
                <NewsItemTitle>{item.title}</NewsItemTitle>
                <NewsItemMeta>{item.meta}</NewsItemMeta>
              </NewsItemBody>
              <NewsItemAction>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v4M12 16h.01" />
                </svg>
              </NewsItemAction>
            </NewsItem>
          ))
        ) : (
          <PlaceholderContent>No items in this category</PlaceholderContent>
        )}
      </NewsFeedList>
    </NewsFeed>
  );
};

// --- Styled ---

const Page = styled.div`
  max-width: 1200px;
`;

const Greeting = styled.div`
  margin: 0 0 24px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const GreetingLeft = styled.div``;

const GreetingTitle = styled.h1`
  font-family: var(--font-serif);
  font-size: 28px;
  font-weight: 400;
  letter-spacing: -0.02em;
  line-height: 1.1;
  margin: 0;

  strong {
    font-weight: 400;
  }

  ${media.md} {
    font-size: 38px;
  }
`;

const GreetingMeta = styled.div`
  color: var(--ink-faint);
  font-size: 14px;
  margin-top: 8px;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  margin-bottom: 22px;

  ${media.sm} {
    grid-template-columns: 1fr 1fr;
  }

  ${media.lg} {
    grid-template-columns: 1.4fr 1fr 1fr;
  }
`;

const PlaceholderSection = styled.div`
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 20px;
  margin-bottom: 18px;
`;

const PlaceholderContent = styled.div`
  color: var(--ink-faint);
  font-size: 13px;
  padding: 32px 0;
  text-align: center;
`;

// --- Latest Market Surveys ---

const SectionEyebrow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin: 28px 0 12px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-weight: 600;
  color: var(--ink);
`;

const EyebrowMeta = styled.span`
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  font-size: 12px;
  color: var(--ink-faint);
`;

const MarketStrip = styled.section`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  margin-bottom: 24px;

  ${media.sm} {
    grid-template-columns: 1fr 1fr;
  }

  ${media.lg} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const MarketCard = styled.div`
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  overflow: hidden;
  cursor: pointer;
  transition:
    box-shadow 0.15s ease,
    transform 0.15s ease;

  &:hover {
    box-shadow: var(--shadow-md);
    transform: translateY(-1px);
  }
`;

const MarketThumb = styled.div<{ $bg: string }>`
  height: 100px;
  background: ${({ $bg }) =>
    $bg ? `url(${$bg}) center/cover` : 'var(--line)'};
`;

const MarketMeta = styled.div`
  padding: 10px 12px;
`;

const MarketRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const MarketName = styled.span`
  font-weight: 600;
  font-size: 13px;
`;

const MarketBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 2px 6px;
  border-radius: 3px;
  background: var(--olive-soft);
  color: var(--olive);
`;

const MarketDate = styled.div`
  font-size: 11px;
  color: var(--ink-faint);
  margin-top: 4px;
`;

// --- What's Happening ---

const HomeSplit = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin-top: 8px;

  ${media.lg} {
    grid-template-columns: 1.6fr 1fr;
  }
`;

const NewsFeed = styled.section`
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  overflow: hidden;
`;

const NewsFeedHead = styled.div`
  padding: 14px 16px 0;
`;

const NewsFeedTitle = styled.div`
  font-family: var(--font-serif);
  font-size: 20px;
  font-weight: 400;
  margin-bottom: 12px;
`;

const FeedTabs = styled.div`
  display: flex;
  gap: 4px;
  overflow-x: auto;
  border-bottom: 1px solid var(--line-soft);
  padding: 0;
`;

const FeedTab = styled.button<{ $active: boolean; $dotColor?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ $active }) => ($active ? 'var(--ink)' : 'var(--ink-faint)')};
  background: none;
  border: none;
  border-bottom: 2px solid
    ${({ $active }) => ($active ? 'var(--terracotta)' : 'transparent')};
  cursor: pointer;
  white-space: nowrap;
  transition:
    color 0.15s,
    border-color 0.15s;

  &:hover {
    color: var(--ink);
  }
`;

const TabDot = styled.span<{ $color: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

const TabCount = styled.span`
  font-size: 10px;
  font-weight: 600;
  background: var(--paper);
  border-radius: 8px;
  padding: 1px 5px;
  color: var(--ink-faint);
`;

const NewsFeedList = styled.div`
  max-height: 420px;
  overflow-y: auto;
`;

const NewsItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--line-soft);
  cursor: pointer;
  transition: background 0.1s;

  &:hover {
    background: var(--paper);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const catColors: Record<string, string> = {
  press: 'var(--indigo)',
  notations: 'var(--amber)',
  distribution: 'var(--olive)',
  alerts: 'var(--crimson)'
};

const NewsItemDot = styled.span<{ $cat: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 5px;
  flex-shrink: 0;
  background: ${({ $cat }) => catColors[$cat] || 'var(--ink-faint)'};
`;

const NewsItemBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const NewsItemTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NewsItemMeta = styled.div`
  font-size: 11px;
  color: var(--ink-faint);
  margin-top: 2px;
`;

const NewsItemLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-faint);
  margin-bottom: 2px;
`;

const NewsItemAction = styled.div`
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  color: var(--ink-faint);
  cursor: pointer;
  border-radius: 50%;
  transition: background 0.1s;

  &:hover {
    background: var(--paper);
    color: var(--ink);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

// --- Next Surveys ---

const UpcomingSection = styled.section`
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  overflow: hidden;
`;

const UpcomingList = styled.div``;

const UpcomingItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--line-soft);
  cursor: pointer;
  transition: background 0.1s;

  &:hover {
    background: var(--paper);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const UpcomingIcon = styled.div`
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  color: var(--ink-faint);
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const UpcomingBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const UpcomingName = styled.div`
  font-size: 13px;
  font-weight: 500;
`;

const UpcomingDate = styled.div`
  font-size: 11px;
  color: var(--ink-faint);
  margin-top: 1px;
`;

const UpcomingPip = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: var(--terracotta);
  white-space: nowrap;
`;
